import React, { useState } from 'react';
import './OrderStatusDesignShowcase.scss';
import OrderStatusIcons from './OrderStatusIcons';

export function OrderStatusDesignShowcase() {
    const [selectedDesign, setSelectedDesign] = useState(null);

    // Design variations data
    const designs = [
        {
            id: 1,
            name: 'Vertical Timeline Classic',
            description: 'Traditional vertical timeline with connected nodes',
            component: 'Design1'
        },
        {
            id: 2,
            name: 'Horizontal Progress Bar',
            description: 'Horizontal progress bar with status indicators',
            component: 'Design2'
        },
        {
            id: 3,
            name: 'Card-Based Status',
            description: 'Individual cards for each status stage',
            component: 'Design3'
        },
        {
            id: 4,
            name: 'Minimal Dots',
            description: 'Clean minimal design with dots and lines',
            component: 'Design4'
        },
        {
            id: 5,
            name: 'Stepper Style',
            description: 'Step-by-step stepper design',
            component: 'Design5'
        },
        {
            id: 6,
            name: 'Circular Progress',
            description: 'Circular progress indicator with stages',
            component: 'Design6'
        },
        {
            id: 7,
            name: 'Badge Collection',
            description: 'Status badges in a collection view',
            component: 'Design7'
        },
        {
            id: 8,
            name: 'Timeline with Icons',
            description: 'Icon-rich timeline design',
            component: 'Design8'
        },
        {
            id: 9,
            name: 'Gradient Flow',
            description: 'Gradient-based flow design',
            component: 'Design9'
        },
        {
            id: 10,
            name: 'Compact Horizontal',
            description: 'Compact horizontal layout',
            component: 'Design10'
        },
        {
            id: 11,
            name: 'Animated Wave',
            description: 'Animated wave progress design',
            component: 'Design11'
        },
        {
            id: 12,
            name: 'Modern Minimal',
            description: 'Ultra-minimal modern design',
            component: 'Design12'
        }
    ];

    // Render design component based on ID
    const renderDesign = (designId) => {
        switch (designId) {
            case 1: return <Design1 />;
            case 2: return <Design2 />;
            case 3: return <Design3 />;
            case 4: return <Design4 />;
            case 5: return <Design5 />;
            case 6: return <Design6 />;
            case 7: return <Design7 />;
            case 8: return <Design8 />;
            case 9: return <Design9 />;
            case 10: return <Design10 />;
            case 11: return <Design11 />;
            case 12: return <Design12 />;
            default: return <Design1 />;
        }
    };

    return (
        <div className="order-status-showcase">
            <div className="showcase-header">
                <h1>Order Status Design Showcase</h1>
                <p>Explore {designs.length} different mobile design variations for order status indication</p>
            </div>

            <div className="showcase-grid">
                {designs.map((design) => (
                    <div 
                        key={design.id} 
                        className={`showcase-item ${selectedDesign === design.id ? 'selected' : ''}`}
                        onClick={() => setSelectedDesign(selectedDesign === design.id ? null : design.id)}
                    >
                        <div className="design-preview">
                            <div className="design-label">
                                <h3>{design.name}</h3>
                                <p>{design.description}</p>
                            </div>
                            <div className="design-content">
                                {renderDesign(design.id)}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// Design 1: Vertical Timeline Classic
function Design1() {
    return (
        <div className="design-1">
            <div className="timeline-vertical">
                <div className="timeline-item completed">
                    <div className="timeline-dot">
                        <OrderStatusIcons status="processed" />
                    </div>
                    <div className="timeline-content">
                        <span className="status-label">Processed</span>
                    </div>
                </div>
                <div className="timeline-line completed"></div>
                <div className="timeline-item completed">
                    <div className="timeline-dot">
                        <OrderStatusIcons status="shipped" />
                    </div>
                    <div className="timeline-content">
                        <span className="status-label">Shipped</span>
                    </div>
                </div>
                <div className="timeline-line active"></div>
                <div className="timeline-item active">
                    <div className="timeline-dot">
                        <OrderStatusIcons status="on-the-way" />
                    </div>
                    <div className="timeline-content">
                        <span className="status-label">On the Way</span>
                    </div>
                </div>
                <div className="timeline-line pending"></div>
                <div className="timeline-item pending">
                    <div className="timeline-dot">
                        <OrderStatusIcons status="delivered" />
                    </div>
                    <div className="timeline-content">
                        <span className="status-label">Delivered</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Design 2: Horizontal Progress Bar
function Design2() {
    return (
        <div className="design-2">
            <div className="progress-horizontal">
                <div className="progress-bar">
                    <div className="progress-fill" style={{ width: '75%' }}></div>
                </div>
                <div className="progress-steps">
                    <div className="step completed">
                        <div className="step-icon">
                            <OrderStatusIcons status="processed" />
                        </div>
                        <span>Processed</span>
                    </div>
                    <div className="step completed">
                        <div className="step-icon">
                            <OrderStatusIcons status="shipped" />
                        </div>
                        <span>Shipped</span>
                    </div>
                    <div className="step active">
                        <div className="step-icon">
                            <OrderStatusIcons status="on-the-way" />
                        </div>
                        <span>On Way</span>
                    </div>
                    <div className="step pending">
                        <div className="step-icon">
                            <OrderStatusIcons status="delivered" />
                        </div>
                        <span>Delivered</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Design 3: Card-Based Status
function Design3() {
    return (
        <div className="design-3">
            <div className="status-cards">
                <div className="status-card completed">
                    <div className="card-icon">
                        <OrderStatusIcons status="processed" />
                    </div>
                    <div className="card-content">
                        <h4>Processed</h4>
                        <p>Order confirmed</p>
                    </div>
                    <div className="card-check">✓</div>
                </div>
                <div className="status-card completed">
                    <div className="card-icon">
                        <OrderStatusIcons status="shipped" />
                    </div>
                    <div className="card-content">
                        <h4>Shipped</h4>
                        <p>Package dispatched</p>
                    </div>
                    <div className="card-check">✓</div>
                </div>
                <div className="status-card active">
                    <div className="card-icon">
                        <OrderStatusIcons status="on-the-way" />
                    </div>
                    <div className="card-content">
                        <h4>On the Way</h4>
                        <p>Out for delivery</p>
                    </div>
                    <div className="card-pulse"></div>
                </div>
                <div className="status-card pending">
                    <div className="card-icon">
                        <OrderStatusIcons status="delivered" />
                    </div>
                    <div className="card-content">
                        <h4>Delivered</h4>
                        <p>Pending</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Design 4: Minimal Dots
function Design4() {
    return (
        <div className="design-4">
            <div className="minimal-timeline">
                <div className="minimal-item completed">
                    <div className="minimal-dot"></div>
                    <span>Processed</span>
                </div>
                <div className="minimal-line completed"></div>
                <div className="minimal-item completed">
                    <div className="minimal-dot"></div>
                    <span>Shipped</span>
                </div>
                <div className="minimal-line active"></div>
                <div className="minimal-item active">
                    <div className="minimal-dot"></div>
                    <span>On Way</span>
                </div>
                <div className="minimal-line pending"></div>
                <div className="minimal-item pending">
                    <div className="minimal-dot"></div>
                    <span>Delivered</span>
                </div>
            </div>
        </div>
    );
}

// Design 5: Stepper Style
function Design5() {
    return (
        <div className="design-5">
            <div className="stepper">
                <div className="stepper-step completed">
                    <div className="step-number">1</div>
                    <div className="step-info">
                        <h4>Processed</h4>
                        <p>Order confirmed</p>
                    </div>
                </div>
                <div className="stepper-connector completed"></div>
                <div className="stepper-step completed">
                    <div className="step-number">2</div>
                    <div className="step-info">
                        <h4>Shipped</h4>
                        <p>Package dispatched</p>
                    </div>
                </div>
                <div className="stepper-connector active"></div>
                <div className="stepper-step active">
                    <div className="step-number">3</div>
                    <div className="step-info">
                        <h4>On the Way</h4>
                        <p>Out for delivery</p>
                    </div>
                </div>
                <div className="stepper-connector pending"></div>
                <div className="stepper-step pending">
                    <div className="step-number">4</div>
                    <div className="step-info">
                        <h4>Delivered</h4>
                        <p>Pending</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Design 6: Circular Progress
function Design6() {
    return (
        <div className="design-6">
            <div className="circular-progress">
                <div className="circle-container">
                    <svg className="progress-ring" width="120" height="120">
                        <circle
                            className="progress-ring-circle"
                            stroke="#e9ecef"
                            strokeWidth="8"
                            fill="transparent"
                            r="52"
                            cx="60"
                            cy="60"
                        />
                        <circle
                            className="progress-ring-circle-fill"
                            stroke="#22c55e"
                            strokeWidth="8"
                            fill="transparent"
                            r="52"
                            cx="60"
                            cy="60"
                            strokeDasharray="326.73"
                            strokeDashoffset="81.68"
                            strokeLinecap="round"
                        />
                    </svg>
                    <div className="circle-content">
                        <span className="progress-percent">75%</span>
                        <span className="progress-label">On the Way</span>
                    </div>
                </div>
                <div className="circular-steps">
                    <div className="circular-step completed">✓ Processed</div>
                    <div className="circular-step completed">✓ Shipped</div>
                    <div className="circular-step active">→ On Way</div>
                    <div className="circular-step pending">○ Delivered</div>
                </div>
            </div>
        </div>
    );
}

// Design 7: Badge Collection
function Design7() {
    return (
        <div className="design-7">
            <div className="badge-collection">
                <div className="badge completed">
                    <OrderStatusIcons status="processed" />
                    <span>Processed</span>
                    <div className="badge-check">✓</div>
                </div>
                <div className="badge completed">
                    <OrderStatusIcons status="shipped" />
                    <span>Shipped</span>
                    <div className="badge-check">✓</div>
                </div>
                <div className="badge active">
                    <OrderStatusIcons status="on-the-way" />
                    <span>On the Way</span>
                    <div className="badge-pulse"></div>
                </div>
                <div className="badge pending">
                    <OrderStatusIcons status="delivered" />
                    <span>Delivered</span>
                </div>
            </div>
        </div>
    );
}

// Design 8: Timeline with Icons
function Design8() {
    return (
        <div className="design-8">
            <div className="icon-timeline">
                <div className="icon-item completed">
                    <div className="icon-wrapper">
                        <OrderStatusIcons status="processed" />
                    </div>
                    <div className="icon-label">Processed</div>
                    <div className="icon-time">2 days ago</div>
                </div>
                <div className="icon-line completed"></div>
                <div className="icon-item completed">
                    <div className="icon-wrapper">
                        <OrderStatusIcons status="shipped" />
                    </div>
                    <div className="icon-label">Shipped</div>
                    <div className="icon-time">1 day ago</div>
                </div>
                <div className="icon-line active"></div>
                <div className="icon-item active">
                    <div className="icon-wrapper">
                        <OrderStatusIcons status="on-the-way" />
                    </div>
                    <div className="icon-label">On the Way</div>
                    <div className="icon-time">Today</div>
                </div>
                <div className="icon-line pending"></div>
                <div className="icon-item pending">
                    <div className="icon-wrapper">
                        <OrderStatusIcons status="delivered" />
                    </div>
                    <div className="icon-label">Delivered</div>
                    <div className="icon-time">Expected tomorrow</div>
                </div>
            </div>
        </div>
    );
}

// Design 9: Gradient Flow
function Design9() {
    return (
        <div className="design-9">
            <div className="gradient-flow">
                <div className="flow-item completed">
                    <div className="flow-icon">
                        <OrderStatusIcons status="processed" />
                    </div>
                    <div className="flow-label">Processed</div>
                </div>
                <div className="flow-connector completed"></div>
                <div className="flow-item completed">
                    <div className="flow-icon">
                        <OrderStatusIcons status="shipped" />
                    </div>
                    <div className="flow-label">Shipped</div>
                </div>
                <div className="flow-connector active"></div>
                <div className="flow-item active">
                    <div className="flow-icon">
                        <OrderStatusIcons status="on-the-way" />
                    </div>
                    <div className="flow-label">On Way</div>
                </div>
                <div className="flow-connector pending"></div>
                <div className="flow-item pending">
                    <div className="flow-icon">
                        <OrderStatusIcons status="delivered" />
                    </div>
                    <div className="flow-label">Delivered</div>
                </div>
            </div>
        </div>
    );
}

// Design 10: Compact Horizontal
function Design10() {
    return (
        <div className="design-10">
            <div className="compact-horizontal">
                <div className="compact-item completed">
                    <div className="compact-dot"></div>
                    <span>P</span>
                </div>
                <div className="compact-line completed"></div>
                <div className="compact-item completed">
                    <div className="compact-dot"></div>
                    <span>S</span>
                </div>
                <div className="compact-line active"></div>
                <div className="compact-item active">
                    <div className="compact-dot"></div>
                    <span>W</span>
                </div>
                <div className="compact-line pending"></div>
                <div className="compact-item pending">
                    <div className="compact-dot"></div>
                    <span>D</span>
                </div>
            </div>
            <div className="compact-labels">
                <span>Processed</span>
                <span>Shipped</span>
                <span>On Way</span>
                <span>Delivered</span>
            </div>
        </div>
    );
}

// Design 11: Animated Wave
function Design11() {
    return (
        <div className="design-11">
            <div className="wave-progress">
                <div className="wave-container">
                    <div className="wave-fill" style={{ height: '75%' }}>
                        <svg className="wave-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                            <path d="M0,50 Q25,30 50,50 T100,50 L100,100 L0,100 Z" fill="rgba(34, 197, 94, 0.3)"></path>
                        </svg>
                    </div>
                </div>
                <div className="wave-steps">
                    <div className="wave-step completed">
                        <OrderStatusIcons status="processed" />
                        <span>Processed</span>
                    </div>
                    <div className="wave-step completed">
                        <OrderStatusIcons status="shipped" />
                        <span>Shipped</span>
                    </div>
                    <div className="wave-step active">
                        <OrderStatusIcons status="on-the-way" />
                        <span>On Way</span>
                    </div>
                    <div className="wave-step pending">
                        <OrderStatusIcons status="delivered" />
                        <span>Delivered</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Design 12: Modern Minimal
function Design12() {
    return (
        <div className="design-12">
            <div className="modern-minimal">
                <div className="minimal-progress">
                    <div className="minimal-bar">
                        <div className="minimal-fill" style={{ width: '75%' }}></div>
                    </div>
                </div>
                <div className="minimal-statuses">
                    <div className="minimal-status completed">
                        <div className="status-indicator"></div>
                        <span>Processed</span>
                    </div>
                    <div className="minimal-status completed">
                        <div className="status-indicator"></div>
                        <span>Shipped</span>
                    </div>
                    <div className="minimal-status active">
                        <div className="status-indicator"></div>
                        <span>On Way</span>
                    </div>
                    <div className="minimal-status pending">
                        <div className="status-indicator"></div>
                        <span>Delivered</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

